import { yupResolver } from "@hookform/resolvers/yup";
import { RowSelectionState } from "@tanstack/react-table";
import { useContext, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import Loading from "../../Loading";
import {
  useQueryBillingMinimums,
  useQueryFeeStructures,
} from "../../billing/lib";
import { NotificationContext } from "../../Notifications";
import FormFieldError from "../../components/FormFieldError";
import SubmitButton from "../../components/SubmitButton";
import { serverError } from "../../lib/api";
import { toISODateNoTimezone } from "../../lib/date";
import { getSchemaFieldLabel } from "../../lib/forms";
import HouseholdGroupHouseholdPicker from "./HouseholdGroupHouseholdPicker";
import { useCreateHouseholdGroup } from "./lib";

type CreateHouseholdGroupForm = {
  name: string;
  feeStructureId: string;
  billingMinimumId: string;
  effectiveDate: string;
};

const schema: yup.ObjectSchema<CreateHouseholdGroupForm> = yup.object({
  name: yup.string().required().label("Name"),
  // A group always carries a fee structure: the select has no "None" option.
  feeStructureId: yup.string().required().label("Assigned Fee Structure"),
  billingMinimumId: yup.string().defined().label("Assigned Billing Minimum"),
  effectiveDate: yup.string().required().label("Effective Date"),
});

const byName = (a: { name: string }, b: { name: string }) =>
  a.name < b.name ? -1 : a.name > b.name ? 1 : 0;

const CreateHouseholdGroupFormBody = ({
  feeStructures,
  billingMinimums,
  onHide,
}: {
  feeStructures: { feeStructureId: number; name: string }[];
  billingMinimums: { id: number; name: string }[];
  onHide: () => void;
}) => {
  const sortedFeeStructures = [...feeStructures].sort(byName);
  const sortedBillingMinimums = [...billingMinimums].sort(byName);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateHouseholdGroupForm>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    // The select shows its first fee structure as chosen; the form value must
    // be the same one.
    defaultValues: {
      name: "",
      feeStructureId: sortedFeeStructures[0]?.feeStructureId.toString() ?? "",
      billingMinimumId: "",
      effectiveDate: toISODateNoTimezone(new Date()),
    },
  });
  const effectiveDate = watch("effectiveDate");

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectedHouseholdIds = Object.keys(rowSelection).filter(
    (householdId) => rowSelection[householdId],
  );
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createHouseholdGroup = useCreateHouseholdGroup();
  const notificationContext = useContext(NotificationContext);

  const onValid = async (data: CreateHouseholdGroupForm) => {
    if (selectedHouseholdIds.length < 2) {
      return;
    }
    setSubmitError(null);
    try {
      const group = await createHouseholdGroup.mutateAsync({
        name: data.name,
        feeStructureId: Number(data.feeStructureId),
        billingMinimumId:
          data.billingMinimumId === "" ? null : Number(data.billingMinimumId),
        households: selectedHouseholdIds.map((householdId) => ({
          householdId: Number(householdId),
          effectiveDate: data.effectiveDate,
        })),
      });
      notificationContext.pushNotification({
        id: `household-group-${group.householdGroupId}`,
        header: "Household Group Created",
        body: `${data.name} created`,
        variant: "success",
      });
      onHide();
    } catch (err) {
      console.error("Failed to create household group", err);
      const message = serverError(err)?.body?.message;
      setSubmitError(
        typeof message === "string" ? message : `${data.name} was not created`,
      );
    }
  };

  return (
    <Form
      onSubmit={(event) => {
        setIsSubmitAttempted(true);
        return handleSubmit(onValid)(event);
      }}
    >
      <Modal.Body>
        {submitError === null ? null : (
          <Alert variant="danger">{submitError}</Alert>
        )}
        <Form.Group className="mb-3" controlId="form-name">
          <Form.Label>{getSchemaFieldLabel(schema.fields.name)}</Form.Label>
          <Form.Control type="text" {...register("name")} />
          <FormFieldError field={errors.name} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="form-feeStructureId">
          <Form.Label>
            {getSchemaFieldLabel(schema.fields.feeStructureId)}
          </Form.Label>
          <Form.Select {...register("feeStructureId")}>
            {sortedFeeStructures.map((feeStructure) => (
              <option
                key={feeStructure.feeStructureId}
                value={feeStructure.feeStructureId}
              >
                {feeStructure.name}
              </option>
            ))}
          </Form.Select>
          <FormFieldError field={errors.feeStructureId} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="form-billingMinimumId">
          <Form.Label>
            {getSchemaFieldLabel(schema.fields.billingMinimumId)}
          </Form.Label>
          <Form.Select {...register("billingMinimumId")}>
            <option value="">None</option>
            {sortedBillingMinimums.map((billingMinimum) => (
              <option key={billingMinimum.id} value={billingMinimum.id}>
                {billingMinimum.name}
              </option>
            ))}
          </Form.Select>
          <FormFieldError field={errors.billingMinimumId} />
        </Form.Group>
        <Form.Group className="mb-3" controlId="form-effectiveDate">
          <Form.Label>
            {getSchemaFieldLabel(schema.fields.effectiveDate)}
          </Form.Label>
          <Form.Control type="date" {...register("effectiveDate")} />
          <FormFieldError field={errors.effectiveDate} />
        </Form.Group>
        <HouseholdGroupHouseholdPicker
          effectiveDate={effectiveDate}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          multiple
        />
        {isSubmitAttempted && selectedHouseholdIds.length < 2 ? (
          <Form.Text className="text-danger d-block">
            Choose two or more households.
          </Form.Text>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <SubmitButton label="Create" isSubmitting={isSubmitting} />
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Form>
  );
};

const CreateHouseholdGroupDialogContent = ({
  onHide,
}: {
  onHide: () => void;
}) => {
  const {
    isPending: isPendingFeeStructures,
    isError: isErrorFeeStructures,
    data: dataFeeStructures,
  } = useQueryFeeStructures();

  const {
    isPending: isPendingBillingMinimums,
    isError: isErrorBillingMinimums,
    data: dataBillingMinimums,
  } = useQueryBillingMinimums();

  return isPendingFeeStructures || isPendingBillingMinimums ? (
    <Modal.Body>
      <Loading />
    </Modal.Body>
  ) : isErrorFeeStructures || isErrorBillingMinimums ? (
    <Modal.Body>
      <Alert variant="danger">An error occurred</Alert>
    </Modal.Body>
  ) : (
    <CreateHouseholdGroupFormBody
      feeStructures={dataFeeStructures?.data ?? []}
      billingMinimums={dataBillingMinimums?.data ?? []}
      onHide={onHide}
    />
  );
};

/**
 * The dialog that creates a household group with its first two or more
 * households (s-household-groups-group-level-019f18f8, § Group creation).
 * Its content mounts only while it is shown, so no request leaves while it is
 * closed.
 */
const CreateHouseholdGroupDialog = ({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>Create Household Group</Modal.Title>
    </Modal.Header>
    {show ? <CreateHouseholdGroupDialogContent onHide={onHide} /> : null}
  </Modal>
);

export default CreateHouseholdGroupDialog;
